/* eslint-disable import/no-cycle */
import { call, select, takeLatest } from 'redux-saga/effects';
import { createRoutine } from 'redux-saga-routines';
import { v4 } from 'uuid';
import api from 'utils/api';
import { BASE_API_URL } from 'api/constants';
import firebase, { getUserProps } from 'utils/auth/firebase';
import logger from 'utils/logger';
import { getPersistentData, SESSION_PERSIST_KEY } from 'utils/localstorage';
import { getFavoritesSaga } from '../favorites';
import { initRealmSaga } from '../realm';
import { getHistorySaga } from '../history';
import { getActiveTrackIndex, getQueue } from '../player';
import { getUserId } from '../auth';

export const callPingRoutine = createRoutine('CALL_PING');
export const initRealmDataRoutine = createRoutine('INIT_REALM_DATA');
export const updateUserDataRoutine = createRoutine('UPDATE_USER_DATA');
export const updateUserSubscriptionRoutine = createRoutine('UPDATE_USER_SUBSCRIPTIONS');
export const updateUserEntitlementsRoutine = createRoutine('UPDATE_USER_ENTITLEMENTS');

export const initialState = Object.freeze({
  config: null,
  userData: {},
  userSubscriptions: null,
  userEntitlements: null,
});

export const appReducer = (state = initialState, action) => {
  switch (action.type) {
    case updateUserDataRoutine.REQUEST:
      return {
        ...state,
        userData: action.payload,
      };
    case updateUserSubscriptionRoutine.REQUEST:
      return {
        ...state,
        userSubscriptions: action.payload,
      };
    case updateUserEntitlementsRoutine.REQUEST:
      return {
        ...state,
        userEntitlements: action.payload,
      };
    default:
      return { ...state };
  }
};

export const getApp = ({ app }) => app;
export const getConfig = ({ app: { config } }) => config;

export function* initRealmDataSaga() {
  try {
    // Init Realm if available
    const database = yield call(initRealmSaga);
    // Load Favorite/History items from localstorage/Realm DB
    yield call(getFavoritesSaga, { database });
    yield call(getHistorySaga);
  } catch (error) {
    logger.error('initRealmDataSaga', 'Error.', error);
  }
}

export function* callPingSaga({ payload }) {
  const queue = yield select(getQueue);
  const userId = yield select(getUserId);
  const activeIndex = yield select(getActiveTrackIndex);
  const session = getPersistentData(SESSION_PERSIST_KEY);
  const { trackId, assetId } = queue[activeIndex] ?? {};
  const app = yield select(getApp);
  const { permissions } = getUserProps(app);

  try {
    const uuid = v4();
    const data = {
      uuid,
      platform: 'web',
      user_id: userId,
      track_id: trackId,
      asset_id: assetId,
      timestamp: new Date(),
      session_id: session.id,
      length: Math.floor(payload.length),
      position: Math.floor(payload.position),
    };

    firebase.analytics().logEvent('aud_ping', {
      uuid,
      track_id: trackId,
      asset_id: assetId,
      length: data.length,
      position: data.position,
      permissions: permissions || [],
    });

    yield call(async () => api.post(`${BASE_API_URL}/pings/`, data));
  } catch (error) {
    logger.error('callPingSaga', 'Error.', error);
  }
}

export function* watchAppSagas() {
  yield takeLatest(callPingRoutine.request, callPingSaga);
  yield takeLatest(initRealmDataRoutine.request, initRealmDataSaga);
}
